'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { ArrowBigUp, CheckCircle2 } from 'lucide-react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import Avatar from '../common/Avatar';

// Research answers restate the question as a leading heading; the card
// already shows the question, so drop it from the preview.
const stripLeadingHeading = (html: string) => html.replace(/^\s*<h[1-4][^>]*>[\s\S]*?<\/h[1-4]>\s*/i, '');

// The question card's answer: a quiet reply bubble (avatar + author head +
// real post content), clamped and typographically compacted so it never
// competes with the question. The thread page pins the full answer instead.
const TopAnswerPreview = t0 => {
  const $ = _c(24);
  const {
    answer
  } = t0;
  const {
    contentUIRenderHandler
  } = useContentUIRenderer();
  const t1 = answer.user.avatarKey ?? null;
  const t2 = answer.user.robohash ?? "";
  const t3 = answer.user.tag === "BOT";
  let t4;
  if ($[0] !== answer.user.id || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    t4 = <Avatar avatarKey={t1} robohash={t2} isBot={t3} size={18} userId={answer.user.id} />;
    $[0] = answer.user.id;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== answer.user.username) {
    t5 = <span className="text-xs font-emphasis text-textPrimary">{answer.user.username}</span>;
    $[5] = answer.user.username;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="flex items-center gap-1 text-[11px] font-emphasis text-green-500"><CheckCircle2 size={12} />Top answer</span>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowBigUp size={14} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== answer.voteCount) {
    t8 = <span className="ml-auto flex items-center gap-0.5 text-xs text-textSecondary">{t7}{answer.voteCount}</span>;
    $[9] = answer.voteCount;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t8) {
    t9 = <div className="flex items-center gap-2">{t4}{t5}{t6}{t8}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== answer.content || $[16] !== answer.id || $[17] !== contentUIRenderHandler) {
    t10 = contentUIRenderHandler(stripLeadingHeading(answer.content), answer.id, false);
    $[15] = answer.content;
    $[16] = answer.id;
    $[17] = contentUIRenderHandler;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <div className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-textSecondary [&_h1]:text-sm [&_h1]:font-emphasis [&_h1]:text-textPrimary [&_h2]:text-sm [&_h2]:font-emphasis [&_h2]:text-textPrimary [&_h3]:text-sm [&_h3]:font-emphasis [&_h3]:text-textPrimary [&_h4]:text-sm [&_h4]:font-emphasis [&_h4]:text-textPrimary [&_p]:my-0">{t10}</div>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t9) {
    t12 = <div className="mt-2 rounded-xl bg-background-secondary px-3 py-2.5">{t9}{t11}</div>;
    $[21] = t11;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default TopAnswerPreview;